import React, { useState, useMemo, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Table } from '../table/table';
import { UwTaskListTableModel, UwTask } from './UwTaskList.table.model';
import { Icon } from '../common/Icon';
import { InputControl } from '../common/InputControl';
import { ZielErgebnisBalken, ZielErgebnisBalkenModel } from '../charts/ZielErgebnisBalken';
import Widget from '../common/Widget';

interface IUwTaskListProps {
	tasks: UwTask[];
}

export const UwTaskList: React.FC<IUwTaskListProps> = observer(({ tasks }) => {
	const [completed, setCompleted] = useState<Set<string>>(new Set());
	const [query, setQuery] = useState('');

	const tm = useMemo(() => {
		const tableModel = new UwTaskListTableModel();
		tableModel.onToggleComplete = (agtId: string) => {
			setCompleted((prev) => {
				const next = new Set(prev);
				if (next.has(agtId)) {
					next.delete(agtId);
				} else {
					next.add(agtId);
				}
				return next;
			});
		};
		return tableModel;
	}, []);

	// Load and filter data into table model
	useEffect(() => {
		let list = tasks.map((task) => ({
			...task,
			completed: completed.has(task.agtId),
		}));

		// Filter
		if (query.trim()) {
			const q = query.trim().toLowerCase();
			list = list.filter((r) => r.agtId.toLowerCase().includes(q) || r.name.toLowerCase().includes(q));
		}

		tm.setRowData(list);
		tm.sort();
	}, [tasks, query, tm, completed]);

	const totals = useMemo(() => {
		const total = tasks.reduce((s, r) => s + r.amount, 0);
		const remaining = tasks.filter((r) => !completed.has(r.agtId)).reduce((s, r) => s + r.amount, 0);
		return {
			total,
			remaining,
			completedSum: total - remaining,
		};
	}, [tasks, completed]);

	const completeCount = completed.size;
	const totalCount = tasks.length;
	const progressPct = totalCount > 0 ? Math.round((completeCount / totalCount) * 100) : 0;

	const progressModel = useMemo(() => new ZielErgebnisBalkenModel(totalCount, completeCount), [totalCount, completeCount]);

	const fmtEUR = (n: number) =>
		n.toLocaleString('de-DE', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});

	const fmtEURwithSym = (n: number) => fmtEUR(n) + ' €';

	return (
		<div className="">
			<div className="grid gap-3 is-col-bigger-right pad-1rem ">
				<div className="page-header content">
					<h1>Aufgabenliste: GS München</h1>
					<p className="color-grey">
						Übertragen Sie die folgenden Vorgänge in das UW-Erfassungssystem. Bearbeiten Sie eine Zeile nach der anderen und markieren Sie sie anschließend als erledigt.
					</p>
				</div>

				{/* Stats */}
				<div className="grid is-col-auto align-self-end">
					<div className="">
						<strong className="color-grey">Summe gesamt</strong>
						<div className="title is-3">{fmtEURwithSym(totals.total)}</div>
					</div>

					<div className="">
						<strong className="color-grey">Offen</strong>
						<div className="title is-3 color-primary">{fmtEURwithSym(totals.remaining)}</div>
						<small>
							{totalCount - completeCount} von {totalCount} Vorgängen
						</small>
					</div>

					<div className="">
						<strong className="color-grey">Erledigt</strong>
						<div className="title is-3 color-green">{fmtEURwithSym(totals.completedSum)}</div>
						<small>
							{completeCount} von {totalCount} Vorgängen
						</small>
					</div>
				</div>
			</div>

			{/* Progress */}
			<div className="pad-1rem">
				<Widget title="Progress" disableCollapse={true}>
					<ZielErgebnisBalken model={progressModel} einheit="Vorgänge" color="is-turquoise" decimals={0} />
				</Widget>
			</div>

			{/* Info box */}
			<div className="pad-1rem">
				<div className="note-box is-turquoise bg-turquoise">
					<span>
						<strong>Tipp:</strong> Klicken Sie auf eine Zelle mit <Icon iconClass="copy" faModifier="far" /> um den Wert in die Zwischenablage zu kopieren.
					</span>
				</div>
			</div>

			<div className="agt-list-container border-top">
				{/* Search */}
				<div className="agentur-filter-container is-collapsed">
					<div className="agentur-filter-count">
						{tm.rows.length} / {tasks.length} Vorgänge
					</div>
					<div className="current-filters">
						<InputControl
							name="fullText"
							label="Textsuche"
							value={query}
							onChange={(_n: string, v: string) => setQuery(v)}
							debounceTime={250}
							placeholder="Freitext"
							icon="search"
							modifier="current-filter is-inline"
						/>
					</div>
				</div>

				{/* Table */}
				<Table tm={tm} stickyHeader={true} stickyFooter={true} />
			</div>
		</div>
	);
});
